import { useMemo, useState } from "react";
import Select from "react-select";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Table from "@components/layout/Table";
import Loading from "@components/shared/Loading";
import Label from "@components/form/Label";
import SearchInput from "@components/form/SearchInput";
import Tag from "@components/shared/Tag";
import ConfirmModal from "@components/shared/ConfirmModal";
import NoteIndicatorBadge from "@components/notes/NoteIndicatorBadge";
import RowNotesModal from "@components/notes/RowNotesModal";
import { useAppContext } from "@context/AppContext";
import { useNoteCounts } from "@hooks/query/useNoteCounts";
import { useMeterUploadsByLevel1Resource } from "@hooks/query/useMeterUploadsByLevel1Resource";
import { useDeleteMeterUploadDraft } from "@hooks/mutation/useDeleteMeterUploadDraft";
import { toastError, toastSuccess } from "@utils/toast";
import ENV from "@config/env";
import { DBTables } from "@utils/constants/dbTables";
import { formatDateTime } from "@utils/formatDateTime";

type Level1WRSUploadListProps = {
  level1Resource: Record<string, any>;
};

// Numeric status codes mirror the API-side MeterUploadStatus / MeterUploadRowStatus enums.
const ApiMeterUploadStatus = {
  Draft: 0,
  PartiallyCompleted: 1,
  Completed: 2,
} as const;

const ApiMeterUploadRowStatus = {
  Draft: 0,
  Success: 1,
  Failed: 2,
} as const;

const STATUS_LABEL_KEYS: Record<number, string> = {
  [ApiMeterUploadStatus.Draft]: "bulk_meter_upload.status.draft",
  [ApiMeterUploadStatus.PartiallyCompleted]:
    "bulk_meter_upload.status.partially_completed",
  [ApiMeterUploadStatus.Completed]: "bulk_meter_upload.status.completed",
};

const STATUS_TAG: Record<number, "info" | "warning" | "success"> = {
  [ApiMeterUploadStatus.Draft]: "info",
  [ApiMeterUploadStatus.PartiallyCompleted]: "warning",
  [ApiMeterUploadStatus.Completed]: "success",
};

const Level1WRSUploadList: React.FunctionComponent<
  Level1WRSUploadListProps
> = ({ level1Resource }) => {
  const { checkPermissions } = useAppContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: meterUploadHistory, isLoading } =
    useMeterUploadsByLevel1Resource(level1Resource?.id);

  const deleteDraft = useDeleteMeterUploadDraft({
    onSuccess: () => {
      toastSuccess(t("bulk_meter_upload.message.draft_deleted"));
    },
    onError: (error: any) => {
      toastError(
        error?.response?.data?.message ??
          t("bulk_meter_upload.message.draft_delete_error"),
      );
    },
  });

  const [filter, setFilter] = useState({ fileName: "" });
  const [deletingDraftId, setDeletingDraftId] = useState<string | null>(null);
  const [openNotesRow, setOpenNotesRow] = useState<{
    id: string;
    label: string;
  } | null>(null);

  const handleConfirmDeleteDraft = async () => {
    if (!deletingDraftId) return;
    try {
      await deleteDraft.mutateAsync(deletingDraftId);
    } finally {
      setDeletingDraftId(null);
    }
  };

  const uploadIds = useMemo(
    () => (meterUploadHistory ?? []).map((u: any) => u.id),
    [meterUploadHistory],
  );
  const { data: noteCounts = {} } = useNoteCounts(
    DBTables.MeterUploads,
    uploadIds,
  );
  const canViewNotes = checkPermissions(["ViewNotes"]);

  if (isLoading) {
    return (
      <div className="pt-20">
        <Loading />
      </div>
    );
  }

  const tableFields = [
    { title: t("common.file_uploaded.file"), name: "title" },
    { title: t("bulk_meter_upload.history.uploaded_by"), name: "uploadedBy" },
    { title: t("bulk_meter_upload.history.uploaded_at"), name: "uploadedAt" },
    { title: t("bulk_meter_upload.history.status"), name: "status" },
    { title: t("bulk_meter_upload.history.rows"), name: "rows" },
    ...(canViewNotes ? [{ title: t("subscriber.notes"), name: "notes" }] : []),
    { title: t("common.action"), name: "action" },
  ];

  const rowCounts = (rows: any[] = []) => {
    const total = rows.length;
    const success = rows.filter(
      r => r.status === ApiMeterUploadRowStatus.Success,
    ).length;
    const failed = rows.filter(
      r => r.status === ApiMeterUploadRowStatus.Failed,
    ).length;
    return { total, success, failed };
  };

  const tableData = meterUploadHistory
    ?.filter(
      (item: any) =>
        !filter.fileName?.length ||
        (item.fileName ?? item.evidences?.[0]?.fileName ?? "")
          .toLowerCase()
          .includes(filter.fileName.toLowerCase()),
    )
    .map((item: any) => {
      const status: number = item.status ?? ApiMeterUploadStatus.Completed;
      const counts = rowCounts(item.rows);
      const fileName = item.fileName ?? item.evidences?.[0]?.fileName ?? "";
      const canResume =
        status === ApiMeterUploadStatus.Draft ||
        status === ApiMeterUploadStatus.PartiallyCompleted;

      return {
        title: fileName,
        uploadedBy: item.uploadedByUser?.name ?? "",
        uploadedAt: formatDateTime(new Date(item.updatedAt ?? item.createdAt)),
        status: (
          <Tag status={STATUS_TAG[status] ?? "warning"}>
            {t(
              STATUS_LABEL_KEYS[status] ??
                STATUS_LABEL_KEYS[ApiMeterUploadStatus.Draft],
            )}
          </Tag>
        ),
        rows:
          counts.total === 0
            ? "—"
            : status === ApiMeterUploadStatus.Draft
              ? `${counts.total}`
              : `${counts.success}/${counts.total}`,
        notes: (
          <NoteIndicatorBadge
            count={noteCounts[item.id]}
            onClick={() =>
              setOpenNotesRow({
                id: item.id,
                label: fileName,
              })
            }
          />
        ),
        action: (() => {
          const options = [
            ...(canResume && checkPermissions(["BulkUploadMeterReadings"])
              ? [
                  {
                    label: t("bulk_meter_upload.resume"),
                    value: "resume",
                    onSelect: () =>
                      navigate(
                        `/polestar/level1wrs/${level1Resource.id}/bulk_meter_upload?draftId=${item.id}`,
                        { state: { level1Resource } },
                      ),
                  },
                ]
              : []),
            // A finalised upload is inspectable read-only by anyone who can see this
            // list (?mode=view relaxes the upload page's permission to ViewLevel1Resource).
            ...(status === ApiMeterUploadStatus.Completed
              ? [
                  {
                    label: t("common.view"),
                    value: "view",
                    onSelect: () =>
                      navigate(
                        `/polestar/level1wrs/${level1Resource.id}/bulk_meter_upload?draftId=${item.id}&mode=view`,
                        { state: { level1Resource } },
                      ),
                  },
                ]
              : []),
            ...(status === ApiMeterUploadStatus.Draft &&
            checkPermissions(["BulkUploadMeterReadings"])
              ? [
                  {
                    label: t("common.delete"),
                    value: "delete",
                    onSelect: () => setDeletingDraftId(item.id),
                  },
                ]
              : []),
            ...(item.evidences?.[0]?.id
              ? [
                  {
                    label: t("common.download"),
                    value: "view",
                    onSelect: () =>
                      Object.assign(document.createElement("a"), {
                        href: `${ENV.API_HOST}/evidences/${item.evidences[0].id}/download`,
                        download: "",
                      }).click(),
                  },
                ]
              : []),
          ];

          return options.length > 0 ? (
            <Select
              placeholder={t("common.actions")}
              options={options}
              onChange={e => e?.onSelect?.()}
              menuPortalTarget={document.body}
              isSearchable={false}
              value={null}
            />
          ) : null;
        })(),
      };
    });

  const handleFilterChange = (field: string, value: any) => {
    setFilter({
      ...filter,
      [field]: value,
    });
  };

  return (
    <>
      <div className="flex justify-between items-end">
        <form className="grid grid-cols-1 gap-2 md:grid-cols-3 md:gap-3 2xl:w-2/3 relative z-20">
          <div>
            <Label htmlFor="fileName">
              {t("bulk_meter_upload.filter.file_name")}
            </Label>
            <SearchInput
              id="fileName"
              onChange={e => handleFilterChange("fileName", e.target.value)}
            />
          </div>
        </form>

        {checkPermissions(["BulkUploadMeterReadings"]) && (
          <Link
            className="btn-secondary text-sm rounded"
            to={`/polestar/level1wrs/${level1Resource?.id}/bulk_meter_upload`}
            state={{ level1Resource }}
          >
            {t("extraction_point.upload_bulk_meter")}
          </Link>
        )}
      </div>
      <Table
        containerClassName="rounded-none md:rounded-none text-sm"
        fields={tableFields}
        data={tableData ?? []}
        stickyHeader
      />

      <ConfirmModal
        open={!!deletingDraftId}
        onClose={() => setDeletingDraftId(null)}
        onConfirm={handleConfirmDeleteDraft}
        isSubmitting={deleteDraft.isLoading}
        confirmText={t("common.delete")}
      >
        {t("bulk_meter_upload.delete_draft_confirm")}
      </ConfirmModal>

      <RowNotesModal
        open={openNotesRow !== null}
        referenceTable={DBTables.MeterUploads}
        referenceId={openNotesRow?.id ?? ""}
        artifactName={openNotesRow?.label}
        onClose={() => setOpenNotesRow(null)}
      />
    </>
  );
};

export default Level1WRSUploadList;
